import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, User, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] p-8" data-testid="contact-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Back to Home</span>
          </button>
          <h1 className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Contact Us</h1>
          <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Get in touch with our team</p>
        </div>

        {/* Contact Info */}
        <div className="card-glass p-6 mb-8">
          <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Sora' }}>Get In Touch</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-white/80">
              <Mail className="w-5 h-5 text-[#FFD60A]" />
              <a href="mailto:info@zenade.network" className="hover:text-[#FFD60A] transition-colors">
                info@zenade.network
              </a>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="card-glass p-8" data-testid="contact-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>
                Your Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3.5 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  required
                  className="input-base w-full pl-11"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>
                Your Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  required
                  className="input-base w-full pl-11"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>
                Subject
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3.5 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  required
                  className="input-base w-full pl-11"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="How can we help?"
                  data-testid="subject-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>
                Message
              </label>
              <textarea
                required
                className="input-base w-full h-32 py-3"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us more about your inquiry..."
                data-testid="message-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="submit-btn"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
